import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Radar, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Register() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "analyst" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (user) nav("/", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await register(form);
      toast.success("Welcome to Sentinel.");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not register");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#060606] text-white p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 mb-8" data-testid="brand-link">
          <div className="w-9 h-9 border border-[#00F0FF] rounded-sm flex items-center justify-center">
            <Radar className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-display font-black text-xl">FORENSIQ</div>
            <div className="text-[10px] font-mono text-[#8E9196] uppercase tracking-widest">Investigation-first cyber-defense</div>
            <div className="text-[9px] font-mono text-[#52525B] uppercase tracking-widest mt-1" data-testid="brand-signature-register">
              Built by <span className="text-[#00F0FF]">Ahamed Fasudeen</span>
            </div>
          </div>
        </Link>

        <h1 className="font-display font-black text-4xl mb-1">Create analyst account</h1>
        <p className="text-[#8E9196] mb-8 text-sm">Provision access to your SOC console.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          {[
            { key: "name", label: "Full name", type: "text", placeholder: "Alex Chen" },
            { key: "email", label: "Email", type: "email", placeholder: "alex@corp.com" },
            { key: "password", label: "Password", type: "password", placeholder: "min 6 chars" },
          ].map((f) => (
            <div key={f.key}>
              <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">{f.label}</label>
              <input
                data-testid={`register-${f.key}`}
                type={f.type} value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2.5 font-mono text-sm outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]"
                required
              />
            </div>
          ))}
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Role</label>
            <div className="flex gap-2">
              {["analyst", "admin"].map((r) => (
                <button
                  type="button" key={r}
                  onClick={() => setForm({ ...form, role: r })}
                  data-testid={`register-role-${r}`}
                  className={`flex-1 py-2 rounded-sm font-mono text-xs uppercase tracking-widest border transition-colors ${
                    form.role === r ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/5" : "border-white/15 text-[#8E9196] hover:text-white hover:border-white/30"
                  }`}
                >{r}</button>
              ))}
            </div>
          </div>

          <button
            data-testid="register-submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black transition-colors py-2.5 rounded-sm font-mono uppercase text-sm tracking-widest disabled:opacity-50"
          >
            {submitting ? "Creating…" : "Create account"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <div className="mt-6 text-xs text-[#8E9196]">
          Already have an account? <Link to="/login" className="text-[#00F0FF] hover:underline" data-testid="link-login">Sign in →</Link>
        </div>
      </div>
    </div>
  );
}
